import { useQuery } from '@apollo/client';
import SearchableSelect from '@ssg/common/Components/SearchableSelect';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import useDebouncedState from '@ssg/common/Hooks/useDebouncedState';
import { loader } from 'graphql.macro';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchUserPermissions } from '../../GraphQL';
import { getGroupPermissionsList } from './GroupPermissionsHelper';
import Box from '../../Components/Layout/Box';

const SEARCH_USERS = loader('../../GraphQL/GroupPermission/SearchUserPermissions.gql');

const UserPermissionsEvaluator: React.FC = () => {
	const { t } = useTranslation();

	const [searchText, setSearchText] = useDebouncedState('', 100);
	const [selectedUser, setSelectedUser] = React.useState<SearchUserPermissions['searchUsers'][number]>();

	const { control } = useForm();

	const { loading, data } = useQuery<SearchUserPermissions>(SEARCH_USERS, {
		variables: {
			searchText: searchText,
		},
		skip: searchText.length === 0,
	});
	const options = React.useMemo<SelectOption[]>(
		() =>
			(data?.searchUsers ?? []).map(u => ({
				label: `${u.name} (${u.email})`,
				value: u.id,
			})),
		[data?.searchUsers],
	);

	const categorizedPermissions = React.useMemo(() => {
		if (typeof selectedUser === 'undefined') return [];

		return getGroupPermissionsList(selectedUser.permissions).filter(group => group.items.length > 0);
	}, [selectedUser]);

	return (
		<Box title="common.userPermissions" full>
			<div className="p-4">
				<SearchableSelect
					control={control}
					name="user"
					title="common.user"
					options={options}
					searchFn={searchText => setSearchText(searchText)}
					onSelect={value => {
						const user = data?.searchUsers.find(u => u.id === value);
						if (typeof user !== 'undefined') {
							setSelectedUser(user);
						}
					}}
					onBlur={() => undefined}
					minInputLength={2}
					isLoading={loading}
					className="w-full"
				/>

				<hr className="mt-2 mb-1" />

				{typeof selectedUser === 'undefined' ? (
					<div className="h-40">{t('groupPermissions.noUserSelected')}</div>
				) : (
					<>
						<div className="mb-4">
							<span className="text-lg font-semibold">{selectedUser.name}</span>
							<span className="text-blue ml-2">({selectedUser.email})</span>
							{selectedUser.groupPermissions.length > 0 && (
								<span className="text-blue ml-2">— {selectedUser.groupPermissions.map(gp => gp.name).join(', ')}</span>
							)}
						</div>

						{categorizedPermissions.length === 0 ? (
							<div className="text-blue">{t('groupPermissions.noPermissions')}</div>
						) : (
							<div className="flex flex-wrap">
								{categorizedPermissions.map(group => (
									<div key={group.name} className="w-1/5 pr-5 pb-4">
										<div className="text-blue mb-1 text-lg font-semibold">{group.name}</div>
										<ul>
											{group.items.map(item => (
												<li key={item.name} className={item.selected ? 'py-0.5' : 'py-0.5 text-gray-400 line-through'}>
													{item.text}
												</li>
											))}
										</ul>
									</div>
								))}
							</div>
						)}
					</>
				)}
			</div>
		</Box>
	);
};

export default UserPermissionsEvaluator;